import {defineStore} from 'pinia';
import jwtDecode from "jwt-decode";
import {useLocalStorage, useStorage} from '@vueuse/core'
import Network from "~/utils/network";

const JWT_STORAGE_KEY = "instacash_bnpl_af_jwt";
const AUTH_HEADER = "X-Auth-Token";

const endpoints = {
    login: "/bulwark/customer/auth/login",
    register: "/customer/register",
    completeRegistration: "/bulwark/customer/auth/complete-registration",
    completeLogin: "/bulwark/customer/auth/complete-login",
    confirmEmail: "/bulwark/customer/auth/confirm-email",
    requestPwdChange: "/bulwark/customer/auth/request-password-change",
    finishPwdChange: "/bulwark/customer/auth/change-password",
    logout: "/bulwark/customer/auth/logout",
};
const claims = {
    refreshToken: "rfsht",
    expiry: "exp",
};

const parseJwt = (token) => {
    // src: https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript
    if (!token) return null;
    return jwtDecode(token);
};

/**
 * The session is considered logged in (fully) if the JWT is present and not expired
 */
const isLoggedIn = (jsonJwt) => {
    let now = Date.now();
    return (
        !!jsonJwt && !!jsonJwt[claims.expiry] && now < jsonJwt[claims.expiry] * 1000
    );
};

/**
 * The session is "ready" and considered (roughly) to be loggedIn if the JWT is present and has a refresh-token
 * or if the JWT is not expired
 */
const isReady = (jsonJwt) => {
    return isLoggedIn(jsonJwt) || (!!jsonJwt && !!jsonJwt[claims.refreshToken]);
};

export const useSessionStore = defineStore('session', {
    /**
     * Inits the session state from the JWT stored in LocalStorage
     * If the JWT cannot be considered "ready" it is not loaded
     */
    state: () => {
        let stateParams = {
            encodedJwt: useStorage(JWT_STORAGE_KEY, ""),
            savedLocalPosition: useStorage('instacash_bnpl_savedLocalPosition', "LOGIN"),
            jsonJwt: null,
            isReady: this.jsonJwt ? isReady(this.jsonJwt) : false,
            secondFactor: undefined,
            nonce: "",
        };
        if (!this.isReady) {
            // No usable JWT detected on token
            this.jsonJwt = {};
            this.encodedJwt = ""; // reset the savedJwt
        }
        return stateParams;
    },
    hydrate(state, initialState) {
        state.encodedJwt = useLocalStorage(JWT_STORAGE_KEY, "");
        state.jsonJwt = parseJwt(state.encodedJwt);
    },

    actions: {
        // checks if current jwt is for the financier in path
        initStore(financer = null) {
            if (financer && this.jsonJwt && this.jsonJwt.fin) {
                if (this.jsonJwt.fin !== financer) {
                    this.logout();
                }
            }
        },
        refreshJwt(jwt) {
            if (jwt) {
                try {
                    let jsonJwt = parseJwt(jwt);
                    this.encodedJwt = jwt;
                    this.jsonJwt = jsonJwt;
                    this.isReady = true;
                } catch (error) {
                    throw "Unable to parse given JWT! - refreshJWT";
                }
            } else {
                throw "No JWT in Login response!";
            }
        },
        async login(loginRequest) {
            try {
                this.logout();
                let response = await Network.http.raw(endpoints.login, {method: 'POST', body: loginRequest});
                let token = await response.headers.get(AUTH_HEADER.toLowerCase());
                if (token) {
                    try {
                        let jsonJwt = parseJwt(token);
                        this.encodedJwt = token;
                        this.jsonJwt = jsonJwt;
                        this.isReady = true;
                        // Login successful
                    } catch (error) {
                        throw "Unable to parse given JWT! ";
                    }
                } else if (!response._data.secondFactorResponse) {
                    throw "No JWT in Login response!";
                }
                return response._data;
            } catch (e) {
                console.log('Error on login!');
                this.logout();
                return Promise.reject(e);
            }
        },
        logout() {
            this.encodedJwt = "";
            this.jsonJwt = {};
            this.isReady = false;
        },
        async register(registration) {
            try {
                this.encodedJwt = "";
                this.jsonJwt = {};
                this.isReady = false;
                let response = await Network.http.raw(endpoints.register, {method: 'POST', body: registration});
                return response.data;
            } catch (e) {
                // Error on register
                this.encodedJwt = "";
                this.jsonJwt = {};
                this.isReady = false;
                return Promise.reject(e);
            }
        },
        async finishLogin(loginRequest) {
            try {
                this.encodedJwt = "";
                this.jsonJwt = {};
                this.isReady = false;
                let response = await Network.http.raw(endpoints.completeLogin, {method: 'POST', body: loginRequest});
                let token = response.headers.get(AUTH_HEADER.toLowerCase());
                if (token) {
                    try {
                        let jsonJwt = parseJwt(token);
                        this.encodedJwt = token;
                        this.jsonJwt = jsonJwt;
                        this.isReady = true;
                        // Login successful
                    } catch (error) {
                        throw "Unable to parse given JWT!";
                    }
                } else {
                    throw "No JWT in Login response!";
                }
                return response._data;
            } catch (e) {
                // Error on login
                this.encodedJwt = "";
                this.jsonJwt = {};
                this.isReady = false;
                return Promise.reject(e);
            }
        },
        async confirmEmail({}, request) {
            try {
                await Network.http.raw(endpoints.confirmEmail, {method: 'POST', body: request});
            } catch (e) {
                return Promise.reject(e);
            }
        },
        async finishRegistration(registration) {
            try {
                this.encodedJwt = "";
                this.jsonJwt = {};
                this.isReady = false;
                let response = await Network.http.raw(endpoints.completeRegistration, {method: 'POST', body: registration});
                let token = response.headers.get(AUTH_HEADER.toLowerCase());
                if (token) {
                    try {
                        let jsonJwt = parseJwt(token);
                        this.encodedJwt = token;
                        this.jsonJwt = jsonJwt;
                        this.isReady = true;
                        // Login successful
                    } catch (error) {
                        throw "Unable to parse given JWT!";
                    }
                } else {
                    throw "No JWT in Login response!";
                }
                return Promise.resolve(true);
            } catch (e) {
                //logger.warn("Error on login");
                //logger.warn(e);
                this.encodedJwt = "";
                this.jsonJwt = {};
                this.isReady = false;
                return Promise.reject(e);
            }
        },
        async requestPwdChange(request) {
            try {
                let response = await Network.http.raw(endpoints.requestPwdChange, {method: 'POST', body: request});
                return response.data;
            } catch (e) {
                this.encodedJwt = "";
                this.jsonJwt = {};
                this.isReady = false;
                return Promise.reject(e);
            }
        },
        async finishPwdChange(solution) {
            try {
                let response = await Network.http.raw(endpoints.finishPwdChange, {method: 'POST', body: solution});
                let token = response.headers.get(AUTH_HEADER.toLowerCase());
                if (token) {
                    try {
                        let jsonJwt = parseJwt(token);
                        this.encodedJwt = token;
                        this.jsonJwt = jsonJwt;
                        this.isReady = true;
                        // Password change successful
                    } catch (error) {
                        throw "Unable to parse given JWT!";
                    }
                } else {
                    throw "No JWT in Login response!";
                }
                return response.data;
            } catch (e) {
                this.encodedJwt = "";
                this.jsonJwt = {};
                this.isReady = false;
                return Promise.reject(e);
            }
        },
    },
    getters: {
        isLoggedIn() {
            return isLoggedIn(this.jsonJwt);
        },
        isAuthReady() {
            return this.isReady;
        },
        jwt() {
            return this.encodedJwt;
        },
        userId() {
            if (isReady(this.jsonJwt)) {
                return this.jsonJwt.sub;
            } else {
                return "UNKNOWN";
            }
        },
        displayName() {
            if (isReady(this.jsonJwt)) {
                return this.jsonJwt.fname;
            } else {
                return undefined;
            }
        },
        email() {
            if (isReady(this.jsonJwt)) {
                return this.jsonJwt.email;
            } else {
                return undefined;
            }
        },
        partnerId() {
            if (isReady(this.jsonJwt)) {
                return this.jsonJwt.ptnid;
            } else {
                return 0;
            }
        },
        phone() {
            if (isReady(this.jsonJwt)) {
                return this.jsonJwt.phone;
            } else {
                return undefined;
            }
        },
        publicId() {
            if (isReady(this.jsonJwt)) {
                return this.jsonJwt.publicId;
            } else {
                return undefined;
            }
        },
        getSecondFactor() {
            return this.secondFactor;
        },
        jwtHeader() {
            return {[AUTH_HEADER]: this.encodedJwt};
        },
    }
})